'use client';

import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '../utils/cx';
import { mergePresetClasses } from '../utils/mergePresetClasses';

export type CardTone = 'primary' | 'secondary' | 'brand' | 'success' | 'warning' | 'danger' | 'info';
export type CardVariant = 'default' | 'glass' | 'glass-clear' | 'transparent' | 'outline';
export type CardPreset = 'default' | 'onboarding' | 'glassHeader' | 'glassPanel' | 'glassPanelCompact' | 'glassDock';
export type CardPadding = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type CardElevation = 'none' | 'sm' | 'md' | 'lg';
export type CardAccent = 'none' | 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'purple' | 'gray';
export type CardAccentSide = 'left' | 'top';
export type CardAccentSize = 'sm' | 'md';
export type CardAccentVariant = 'solid' | 'gradient';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  preset?: CardPreset;
  fullWidth?: boolean;
  border?: boolean;

  tone?: CardTone;
  variant?: CardVariant;
  padding?: CardPadding;
  elevation?: CardElevation;

  interactive?: boolean;
  hoverElevation?: CardElevation;

  accent?: CardAccent;
  accentSide?: CardAccentSide;
  accentSize?: CardAccentSize;
  accentVariant?: CardAccentVariant;
  accentInset?: boolean;

  children?: ReactNode;
  className?: string;
}

function paddingClass(p: CardPadding): string {
  switch (p) {
    case 'none':
      return 'p-0';
    case 'xs':
      return 'p-2';
    case 'sm':
      return 'px-3 py-2';
    case 'md':
      return 'p-4';
    case 'lg':
      return 'p-5';
    case 'xl':
      return 'p-6';
    default:
      return 'p-5';
  }
}

function elevationClass(e: CardElevation): string {
  switch (e) {
    case 'none':
      return 'shadow-none';
    case 'md':
      return 'shadow-sm';
    case 'lg':
      return 'shadow-lg';
    case 'sm':
    default:
      return 'shadow-xs';
  }
}

function hoverElevationClass(e: CardElevation): string {
  const base = elevationClass(e);
  return base.startsWith('shadow-') ? (`hover:${base}` as const) : 'hover:shadow-xs';
}

function presetClass(preset: CardPreset): string {
  switch (preset) {
    case 'onboarding':
      return 'flex flex-col justify-between min-h-[min(600px,calc(100dvh-2rem))] sm:min-h-[720px]';
    case 'glassHeader':
      return 'rounded-none border-b border-white/60 bg-white/70 dark:bg-black/25 backdrop-blur-2xl backdrop-saturate-150';
    case 'glassPanel':
      return 'rounded-[1.75rem] bg-surface-glass shadow-sm backdrop-blur-sm';
    case 'glassPanelCompact':
      return 'rounded-[1.5rem] bg-surface-glass shadow-sm backdrop-blur-sm';
    case 'glassDock':
      return 'flex-1 rounded-[1.75rem] bg-surface-glass p-1.5 shadow-lg backdrop-blur-sm';
    case 'default':
    default:
      return '';
  }
}

function toneClass(tone: CardTone, variant: CardVariant): string {
  if (variant === 'glass') {
    return 'bg-white dark:bg-neutral-900';
  }

  if (variant === 'glass-clear') {
    return (
      'bg-transparent ' +
      'supports-[backdrop-filter]:bg-white/15 dark:supports-[backdrop-filter]:bg-black/15 ' +
      'backdrop-blur-2xl backdrop-saturate-125'
    );
  }

  if (variant === 'transparent' || variant === 'outline') return 'bg-transparent';

  switch (tone) {
    case 'secondary':
      return 'bg-secondary';
    case 'brand':
      return 'bg-brand-primary';
    case 'success':
      return 'bg-success-primary';
    case 'warning':
      return 'bg-warning-primary';
    case 'danger':
      return 'bg-error-primary';
    case 'info':
      return 'bg-secondary';
    case 'primary':
    default:
      return 'bg-primary';
  }
}

function borderClass(border: boolean, variant: CardVariant, tone: CardTone): string {
  if (!border) return 'ring-0';

  if (variant === 'outline') {
    switch (tone) {
      case 'danger':
        return 'ring-1 ring-inset ring-red-500/35 dark:ring-red-400/35';
      case 'warning':
        return 'ring-1 ring-inset ring-yellow-500/35 dark:ring-yellow-400/35';
      case 'success':
        return 'ring-1 ring-inset ring-green-500/35 dark:ring-green-400/35';
      case 'brand':
        return 'ring-1 ring-inset ring-indigo-500/35 dark:ring-indigo-400/35';
      case 'info':
        return 'ring-1 ring-inset ring-blue-500/35 dark:ring-blue-400/35';
      case 'secondary':
      case 'primary':
      default:
        return 'ring-1 ring-inset ring-secondary_alt';
    }
  }

  return 'ring-1 ring-secondary_alt ring-inset';
}

function accentColorClass(accent: CardAccent): string {
  switch (accent) {
    case 'brand':
      return 'bg-brand';
    case 'success':
      return 'bg-green-600';
    case 'warning':
      return 'bg-yellow-600';
    case 'danger':
      return 'bg-red-600';
    case 'info':
      return 'bg-blue-600';
    case 'purple':
      return 'bg-purple-600';
    case 'gray':
      return 'bg-gray-300 dark:bg-gray-700';
    case 'none':
    default:
      return 'bg-transparent';
  }
}

function accentGradientClass(accent: CardAccent, side: CardAccentSide): string {
  const dir = side === 'left' ? 'bg-gradient-to-b' : 'bg-gradient-to-r';
  switch (accent) {
    case 'brand':
      return cx(dir, 'from-indigo-400 to-indigo-600');
    case 'success':
      return cx(dir, 'from-emerald-300 to-emerald-500');
    case 'warning':
      return cx(dir, 'from-amber-300 to-amber-500');
    case 'danger':
      return cx(dir, 'from-rose-300 to-rose-500');
    case 'info':
      return cx(dir, 'from-sky-300 to-sky-500');
    case 'purple':
      return cx(dir, 'from-purple-300 to-purple-500');
    case 'gray':
      return cx(dir, 'from-slate-300 to-slate-500');
    case 'none':
    default:
      return 'bg-transparent';
  }
}

function accentGeometryClass(side: CardAccentSide, size: CardAccentSize, inset: boolean): string {
  if (side === 'left') {
    const w = size === 'sm' ? 'w-1' : 'w-2';
    return inset ? cx('left-0 inset-y-3 rounded-r-full', w) : cx('left-0 top-0 h-full', w);
  }

  const h = size === 'sm' ? 'h-1' : 'h-2';
  return inset ? cx('top-0 inset-x-3 rounded-b-full', h) : cx('left-0 top-0 w-full', h);
}

export function Card({
  preset = 'default',
  fullWidth = false,
  border = true,
  tone = 'primary',
  variant = 'default',
  padding = 'lg',
  elevation = 'sm',
  interactive = false,
  hoverElevation = 'md',
  accent = 'none',
  accentSide = 'left',
  accentSize = 'md',
  accentVariant = 'solid',
  accentInset = false,
  className,
  children,
  ...rest
}: CardProps) {
  const hasAccent = accent !== 'none';

  const accentFill =
    accentVariant === 'gradient' ? accentGradientClass(accent, accentSide) : accentColorClass(accent);

  const accentEl = hasAccent ? (
    <div
      aria-hidden="true"
      className={cx(
        'pointer-events-none absolute',
        accentGeometryClass(accentSide, accentSize, accentInset),
        accentFill,
      )}
    />
  ) : null;

  // The glass header owns its spacing in the consuming layout. Other presets
  // continue to use the Card padding API as before.
  const cardPadding = preset === 'glassHeader' ? 'p-0' : paddingClass(padding);

  // Preset and component defaults form the base layer; explicit className overrides them.
  const presetClasses = cx(
    'relative rounded-2xl',
    borderClass(border, variant, tone),
    presetClass(preset),
    toneClass(tone, variant),
    cardPadding,
    elevationClass(elevation),
    fullWidth ? 'w-full' : null,
    interactive && 'cursor-pointer transition-shadow duration-200',
    interactive && hoverElevation ? hoverElevationClass(hoverElevation) : null,
  );

  return (
    <div
      {...rest}
      className={mergePresetClasses(presetClasses, className)}
    >
      {accentEl}
      {children}
    </div>
  );
}

Card.displayName = 'Card';